import {
  autoSignIn,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  deleteUser,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
} from 'aws-amplify/auth'

import { queryClient } from '~/app/providers/http'
import {
  TConfirmResetPasswordInput,
  TConfirmSignInInput,
  TConfirmSignUpInput,
  TEmailInput,
  TSessionDetails,
  TSignInput,
} from '~/features/auth/models'
import { TCustomOAuthState } from '~/models/auth'
import EEnvironmentVariables from '~/models/config'
import { authSessionQueryKey, isAccessTokenExpiringSoon } from '~/utils/auth'

class AuthService {
  public static async signIn({ email: username, password }: TSignInput) {
    return signIn({ username, password })
  }

  public static async signInOidc() {
    return signInWithRedirect({
      provider: {
        custom: import.meta.env[EEnvironmentVariables.VITE_OIDC_NAME],
      },
    })
  }

  public static async signOut(): Promise<void> {
    return signOut({ global: true })
  }

  public static async signUp({ email, password }: TSignInput) {
    return signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
        },
        autoSignIn: true,
      },
    })
  }

  public static async confirmSignUp({ email, code }: TConfirmSignUpInput) {
    return confirmSignUp({
      username: email,
      confirmationCode: code,
    })
  }

  public static async resendSignUpCode({ email }: TEmailInput) {
    return resendSignUpCode({ username: email })
  }

  public static async confirmSignIn({ newPassword }: TConfirmSignInInput) {
    return confirmSignIn({ challengeResponse: newPassword })
  }

  public static async autoSignIn() {
    return autoSignIn()
  }

  public static async resetPassword({ email }: TEmailInput) {
    return resetPassword({ username: email })
  }

  public static async confirmResetPassword({
    email,
    password,
    code,
  }: TConfirmResetPasswordInput) {
    return confirmResetPassword({
      username: email,
      newPassword: password,
      confirmationCode: code,
    })
  }

  public static async signInWithGoogle(customState: TCustomOAuthState) {
    return signInWithRedirect({
      provider: 'Google',
      customState: JSON.stringify(customState),
    })
  }

  public static async deleteUser() {
    return deleteUser()
  }

  // Default is cached: Amplify returns current tokens and auto-refreshes
  // them when expired/near expiry. forceRefresh hits User Pool + Identity
  // Pool and should only be used after Cognito groups change (new user).
  public static async fetchAuthSession(forceRefresh = false) {
    return fetchAuthSession({ forceRefresh })
  }

  public static async getValidAccessToken() {
    const cachedSession = queryClient.getQueryData<TSessionDetails>(
      authSessionQueryKey(),
    )

    if (
      cachedSession?.accessToken &&
      !isAccessTokenExpiringSoon(cachedSession.accessTokenExpiresAt)
    ) {
      return cachedSession.accessToken
    }

    const nextSession = await fetchAuthSession()
    const accessToken = nextSession.tokens?.accessToken?.toString() ?? ''

    if (accessToken && cachedSession) {
      queryClient.setQueryData<TSessionDetails>(authSessionQueryKey(), {
        ...cachedSession,
        accessToken,
        accessTokenExpiresAt: nextSession.tokens?.accessToken?.payload.exp,
      })
    }

    return accessToken
  }

  public static async getCurrentUser() {
    return getCurrentUser()
  }
}

export default AuthService
